import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FilePdf, X } from '@phosphor-icons/react';
import { fmtDate } from './utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const LoginHistoryTab = ({ loginHistory }) => {
  const [userFilter, setUserFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const filtered = useMemo(() => loginHistory.filter(h => {
    if (userFilter) {
      const s = userFilter.toLowerCase();
      const match = (h.name || '').toLowerCase().includes(s) || (h.email || '').toLowerCase().includes(s) || (h.ip || '').includes(s);
      if (!match) return false;
    }
    if (startDate || endDate) {
      if (!h.logged_at) return false;
      const d = new Date(h.logged_at);
      if (startDate && d < new Date(`${startDate}T00:00:00`)) return false;
      if (endDate && d > new Date(`${endDate}T23:59:59`)) return false;
    }
    return true;
  }), [loginHistory, userFilter, startDate, endDate]);

  const handleExportPDF = async () => {
    try {
      const params = new URLSearchParams({ limit: '2000' });
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const res = await axios.get(`${API_URL}/api/pdf-reports/login-history?${params.toString()}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      link.download = `Historique_connexions_${stamp}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export PDF telecharge');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'export PDF');
    }
  };

  return (
    <div>
      <div className="card" style={{ padding: '12px 16px', marginBottom: '12px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text" className="form-input" placeholder="Rechercher par nom, email ou IP..."
          value={userFilter} onChange={(e) => setUserFilter(e.target.value)}
          data-testid="login-history-search"
          style={{ flex: '1 1 220px', minWidth: '180px' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <label style={{ fontSize: '0.8rem', color: '#5E5F66', fontWeight: '600' }}>Du</label>
          <input
            type="date" className="form-input"
            value={startDate} onChange={(e) => setStartDate(e.target.value)}
            data-testid="login-history-start-date"
            style={{ width: '150px' }}
          />
          <label style={{ fontSize: '0.8rem', color: '#5E5F66', fontWeight: '600' }}>au</label>
          <input
            type="date" className="form-input"
            value={endDate} onChange={(e) => setEndDate(e.target.value)}
            data-testid="login-history-end-date"
            style={{ width: '150px' }}
          />
          {(startDate || endDate) && (
            <button
              type="button"
              onClick={() => { setStartDate(''); setEndDate(''); }}
              data-testid="login-history-clear-dates"
              title="Effacer les dates"
              style={{ background: 'none', border: '1px solid #E2E2E6', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', color: '#5E5F66', fontSize: '0.85rem' }}
            >
              <X size={14} />
            </button>
          )}
        </div>
        <button
          className="btn btn-secondary"
          onClick={handleExportPDF}
          disabled={loginHistory.length === 0}
          data-testid="login-history-export-pdf"
          style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
          title="Exporter la selection au format PDF"
        >
          <FilePdf size={16} weight="bold" /> Exporter PDF
        </button>
      </div>
      <div className="card" style={{ overflow: 'auto' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#5E5F66' }}>Aucune connexion enregistree</div>
        ) : (
          <table className="data-table" data-testid="login-history-table">
            <thead><tr><th>Date & heure</th><th>Utilisateur</th><th>Email</th><th>Role</th><th>IP</th><th>Navigateur</th></tr></thead>
            <tbody>
              {filtered.slice(0, 500).map(h => (
                <tr key={h.id}>
                  <td style={{ fontWeight: '600', fontSize: '0.85rem' }}>{fmtDate(h.logged_at)}</td>
                  <td>{h.name || '-'}</td>
                  <td style={{ fontSize: '0.85rem', color: '#5E5F66' }}>{h.email}</td>
                  <td>
                    <span style={{ padding: '2px 8px', borderRadius: '3px', fontSize: '0.72rem', fontWeight: '600', backgroundColor: h.role === 'admin' ? '#E3F2FD' : '#F4F5F7', color: h.role === 'admin' ? '#1565C0' : '#5E5F66' }}>
                      {h.role === 'admin' ? 'Admin' : 'Salarie'}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{h.ip || '-'}</td>
                  <td style={{ fontSize: '0.72rem', color: '#5E5F66', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={h.user_agent}>{h.user_agent || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
